import { useState } from "react";
import Header from "@/components/Header";
import UploadStep from "@/components/UploadStep";
import MappingStep from "@/components/MappingStep";
import ResultsStep from "@/components/ResultsStep";
import ReportDialog from "@/components/ReportDialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle } from "@phosphor-icons/react";

const STEPS = ["Ingest", "Map", "Results"];

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {STEPS.map((label, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <div key={label} className="flex items-center gap-2">
            <div
              className={`flex items-center gap-2 px-3 py-1.5 border font-mono text-xs uppercase tracking-wider transition-colors ${
                active ? "border-primary bg-primary text-white"
                : done ? "border-primary/40 text-primary" : "border-border text-muted-foreground"
              }`}
            >
              {done ? <CheckCircle size={14} weight="fill" /> : <span>{i + 1}</span>}
              {label}
            </div>
            {i < STEPS.length - 1 && <div className="w-6 h-px bg-border" />}
          </div>
        );
      })}
    </div>
  );
}

export default function Workspace() {
  const [step, setStep] = useState(0);
  const [datasetA, setDatasetA] = useState(null);
  const [datasetB, setDatasetB] = useState(null);
  const [keyA, setKeyA] = useState("");
  const [keyB, setKeyB] = useState("");
  const [comparisons, setComparisons] = useState([]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [comparisonId, setComparisonId] = useState(null);
  const [compName, setCompName] = useState("");
  const [report, setReport] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);

  const runComparison = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/comparisons/run", {
        dataset_a_id: datasetA.id,
        dataset_b_id: datasetB.id,
        key_a: keyA,
        key_b: keyB,
        comparisons,
        name: compName?.trim() || `${datasetA.filename} vs ${datasetB.filename}`,
      });
      setResult(data.result);
      setComparisonId(data.id);
      setCompName(data.name);
      setReport(null);
      setStep(2);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Comparison failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <Stepper step={step} />

        {step === 0 && (
          <UploadStep
            datasetA={datasetA}
            datasetB={datasetB}
            setDatasetA={setDatasetA}
            setDatasetB={setDatasetB}
            onNext={() => setStep(1)}
          />
        )}

        {step === 1 && (
          <MappingStep
            datasetA={datasetA}
            datasetB={datasetB}
            keyA={keyA}
            setKeyA={setKeyA}
            keyB={keyB}
            setKeyB={setKeyB}
            comparisons={comparisons}
            setComparisons={setComparisons}
            compName={compName}
            setCompName={setCompName}
            onBack={() => setStep(0)}
            onRun={runComparison}
            running={running}
          />
        )}

        {step === 2 && result && (
          <ResultsStep
            result={result}
            comparisonId={comparisonId}
            name={compName}
            onBack={() => setStep(1)}
            onReport={() => setReportOpen(true)}
          />
        )}
      </main>

      {comparisonId && (
        <ReportDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          comparisonId={comparisonId}
          report={report}
          setReport={setReport}
        />
      )}
    </div>
  );
}
